/**
 * SEC-3 · Lista de silenciados. Local al dispositivo y persistente.
 *
 * **El mute NO pasa por el servidor, y eso es deliberado.** Silenciar a alguien
 * es una preferencia personal, no una acción sobre la sala: si el servidor
 * dejara de entregarle los mensajes a quien silencia, cualquiera podría deducir
 * quién le ha silenciado por el comportamiento del chat, y silenciar pasaría a
 * ser un gesto público con consecuencias sociales. Filtrando en recepción, el
 * silenciado no se entera de nada — que es justo el objetivo.
 *
 * La contrapartida honesta: se guarda por dispositivo, así que no viaja a otro
 * navegador. Sin cuentas no hay forma de hacerlo mejor, y montar una identidad
 * global solo para esto sería peor que el problema.
 *
 * Lo que se guarda es el `muteId` del roster: un identificador opaco **con
 * alcance de sala** (ver `PeerInfo.muteId`). Sobrevive a que el silenciado se
 * reconecte, y no sirve para reconocerle en otra sala.
 */

const STORAGE_KEY = "disco-football-muted";
/**
 * Tope de entradas guardadas. Los `muteId` son por sala, así que la lista crece
 * con cada partida y las entradas viejas dejan de referirse a nadie; sin tope,
 * el almacenamiento se llenaría de identificadores muertos.
 */
const MAX_ENTRIES = 200;

function load(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr: unknown = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((x): x is string => typeof x === "string" && x.length > 0);
  } catch {
    return [];
  }
}

function save(ids: string[]): void {
  try {
    // Se conservan las MÁS RECIENTES: a quien acabas de silenciar es a quien
    // tienes delante ahora mismo.
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids.slice(-MAX_ENTRIES)));
  } catch {
    /* sin almacenamiento (modo privado): el mute vale para esta sesión */
  }
}

export class MuteList {
  private ids: Set<string>;

  constructor() {
    this.ids = new Set(load());
  }

  /** ¿Está silenciado este ocupante? Sin `muteId` no se puede saber → no. */
  isMuted(muteId: string | undefined): boolean {
    return muteId !== undefined && this.ids.has(muteId);
  }

  /** Silencia o deja de silenciar. Devuelve el estado resultante. */
  toggle(muteId: string): boolean {
    if (this.ids.has(muteId)) this.ids.delete(muteId);
    else this.ids.add(muteId);
    save([...this.ids]);
    return this.ids.has(muteId);
  }

  mute(muteId: string): void {
    if (this.ids.has(muteId)) return;
    this.ids.add(muteId);
    save([...this.ids]);
  }

  unmute(muteId: string): void {
    if (!this.ids.delete(muteId)) return;
    save([...this.ids]);
  }

  /** Cuántos hay silenciados (para la UI de ajustes). */
  get size(): number {
    return this.ids.size;
  }

  /** Vacía la lista (botón de "dejar de silenciar a todos"). */
  clear(): void {
    this.ids.clear();
    save([]);
  }
}
